/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import Calendar from '@accelint/icons/calendar';
import {
  DateField as AriaDateField,
  DateInput as AriaDateInput,
  Text as AriaText,
  DateSegment,
  FieldError,
} from 'react-aria-components/DateField';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { Icon } from '../icon';
import { Label } from '../label';
import styles from './styles.module.css';
import type { DateValue } from '@internationalized/date';
import type { DateFieldProps } from './types';

const months = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

/**
 * DateField - A comprehensive date input component with segmented editing
 *
 * Provides accessible date input functionality with separate segments for day,
 * month, and year. Includes calendar icon, validation states, and international
 * date format support with keyboard navigation between segments.
 *
 * @param props - The date field props.
 * @param props.classNames - Custom class names for sub-elements.
 * @param props.description - Helper text displayed below the field.
 * @param props.errorMessage - Error message displayed when invalid.
 * @param props.inputProps - Additional props for the date input element.
 * @param props.label - Label text displayed above the field.
 * @param props.size - Size variant of the field.
 * @param props.shortMonth - When true, displays 3-letter month abbreviation when not editing.
 * @param props.shouldForceLeadingZeros - Whether to force leading zeros in segments.
 * @param props.isDisabled - Whether the field is disabled.
 * @param props.isInvalid - Whether the field is in an invalid state.
 * @param props.isRequired - Whether the field is required.
 * @param props.isReadOnly - Whether the field is read-only.
 * @returns The date field component.
 *
 * @example
 * ```tsx
 * // Basic date field
 * <DateField label="Birth Date" />
 * ```
 *
 * @example
 * ```tsx
 * // Date field with validation
 * <DateField
 *   label="Event Date"
 *   isRequired
 *   isInvalid={hasError}
 *   errorMessage="Please select a valid date"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Date field with custom formatting
 * <DateField
 *   label="Meeting Date"
 *   shortMonth={false}
 *   placeholder="Select meeting date"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Compact date field
 * <DateField
 *   label="Due Date"
 *   size="small"
 *   description="When is this task due?"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Controlled date field
 * <DateField
 *   label="Selected Date"
 *   value={selectedDate}
 *   onChange={setSelectedDate}
 * />
 * ```
 */
export function DateField<T extends DateValue>({
  classNames,
  description: descriptionProp,
  errorMessage: errorMessageProp,
  inputProps,
  label: labelProp,
  size = 'medium',
  shortMonth = true,
  shouldForceLeadingZeros = true,
  isDisabled,
  isInvalid: isInvalidProp,
  isRequired,
  isReadOnly = false,
  ...rest
}: DateFieldProps<T>) {
  const errorMessage = errorMessageProp || null; // Protect against empty string
  const isSmall = size === 'small';

  return (
    <AriaDateField<T>
      {...rest}
      className={composeRenderProps(classNames?.field, (className) =>
        clsx('group/date-field', styles.field, className),
      )}
      shouldForceLeadingZeros={shouldForceLeadingZeros}
      isDisabled={isDisabled}
      isInvalid={isInvalidProp || (errorMessage ? true : undefined)} // Leave uncontrolled if possible to fallback to validation state
      isRequired={isRequired}
      isReadOnly={isReadOnly}
      aria-label={labelProp}
      data-size={size}
    >
      {(
        { isDisabled }, // Rely on internal state, not props, since state could differ from props
      ) => (
        <>
          {!isSmall && labelProp && (
            <Label
              className={clsx(styles.label, classNames?.label)}
              isDisabled={isDisabled}
              isRequired={isRequired}
            >
              {labelProp}
            </Label>
          )}
          <div
            className={clsx(styles.control, classNames?.control)}
            data-readonly={isReadOnly || null}
          >
            {!isReadOnly && size === 'medium' && (
              <Icon>
                <Calendar />
              </Icon>
            )}
            <AriaDateInput
              {...inputProps}
              className={composeRenderProps(classNames?.input, (className) =>
                clsx(styles.input, className),
              )}
            >
              {(segmentProp) => {
                // Remove extra space and punctuation from input display
                if (segmentProp.type === 'literal') {
                  return <>{segmentProp.text === ':' ? ':' : null}</>;
                }

                return (
                  <DateSegment
                    className={composeRenderProps(
                      classNames?.segment,
                      (className) =>
                        clsx(
                          styles.segment,
                          segmentProp.type === 'month' &&
                            shortMonth &&
                            styles.shortMonth,
                          className,
                        ),
                    )}
                    segment={segmentProp}
                  >
                    {({
                      placeholder,
                      text,
                      value,
                      isFocused,
                      isPlaceholder,
                    }) => {
                      if (isPlaceholder) {
                        return placeholder;
                      }

                      return segmentProp.type === 'month' &&
                        shortMonth &&
                        !isFocused
                        ? months[(value ?? 0) - 1]
                        : text;
                    }}
                  </DateSegment>
                );
              }}
            </AriaDateInput>
          </div>
          {descriptionProp && (!(isSmall || isInvalidProp) || isDisabled) && (
            <AriaText
              className={clsx(styles.description, classNames?.description)}
              slot='description'
            >
              {descriptionProp}
            </AriaText>
          )}
          <FieldError
            className={composeRenderProps(classNames?.error, (className) =>
              clsx(styles.error, className),
            )}
          >
            {errorMessage}
          </FieldError>
        </>
      )}
    </AriaDateField>
  );
}
